import { Reveal } from "./Reveal";
import { PARTNERS } from "../data/site";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/files";
import { API } from "../admin/api";

// Each logo sits on a crisp white rounded tile with generous horizontal
// breathing room. The <img> is never stretched or upscaled: `w-auto h-auto`
// combined with max-height/max-width lets the browser render the asset at
// its native resolution (downscaling only when it would overflow the tile),
// so retina/high-DPR screens keep logos sharp.
const UTM = "?utm_source=elinsconsultancy&utm_medium=partner-logo";

const normaliseUrl = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";
  if (/^(mailto:|tel:)/i.test(v)) return v;
  const base = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  // Attach UTM params so the partner can see traffic came from Elin's.
  // We append carefully so we don't trample an existing query string.
  try {
    const u = new URL(base);
    if (!u.searchParams.has("utm_source"))
      u.searchParams.set("utm_source", "elinsconsultancy");
    if (!u.searchParams.has("utm_medium"))
      u.searchParams.set("utm_medium", "partner-logo");
    return u.toString();
  } catch {
    return base + (base.includes("?") ? "&" : "") + UTM.slice(1);
  }
};

const logClick = (p) => {
  const url = `${API}/partners/${p.id}/click`;
  // No visitor data is sent. Just the partner id. Use sendBeacon when available
  // so the request survives the tab navigating away; fall back to fetch with
  // keepalive otherwise.
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url);
      return;
    }
  } catch {
    /* ignore */
  }
  fetch(url, { method: "POST", keepalive: true, mode: "cors" }).catch(() => {});
};

const PartnerLogo = ({ p, index }) => {
  const src = p.logo_url || p.logo;
  const href = normaliseUrl(p.website_url || p.website || p.url);
  const tile = (
    <div className="partners-tile">
      {src ? (
        <img
          src={fileUrl(src)}
          alt={`${p.name} logo`}
          loading="lazy"
          decoding="async"
          className="partners-logo-img"
        />
      ) : (
        <span className="text-[13px] font-semibold tracking-[0.08em] text-forest-deep whitespace-nowrap">
          {p.name}
        </span>
      )}
      <span className="sr-only">
        {p.name}
        {href ? " — opens in a new tab" : ""}
      </span>
    </div>
  );
  const slotClass = "partners-slot flex items-center justify-center px-4 sm:px-5";
  if (href) {
    return (
      <a
        data-testid={`partner-logo-${index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => logClick(p)}
        onAuxClick={(e) => {
          // Middle-click opens in a new tab in most browsers — count that too.
          if (e.button === 1) logClick(p);
        }}
        aria-label={`Visit ${p.name} (opens in a new tab)`}
        title={`Visit ${p.name}`}
        className={`${slotClass} partners-slot-link`}
      >
        {tile}
      </a>
    );
  }
  return (
    <div
      data-testid={`partner-logo-${index}`}
      className={slotClass}
      title={p.name}
    >
      {tile}
    </div>
  );
};

const Strip = ({ partners, hidden, offset }) => (
  <div className="partners-strip" aria-hidden={hidden || undefined}>
    {partners.map((p, i) => (
      <PartnerLogo
        key={`${p.id || p.name}-${offset}-${i}`}
        p={p}
        index={offset + i + 1}
      />
    ))}
  </div>
);

export const Partners = () => {
  const content = useContent();
  const partners = content?.partners?.length ? content.partners : PARTNERS;
  const count = Math.max(partners.length, 1);
  return (
    <section
      data-testid="partners-section"
      className="bg-cream overflow-hidden"
    >
      <Reveal className="text-center pt-16 sm:pt-20 pb-10 px-6">
        <p
          data-testid="partners-eyebrow"
          className="text-[11px] sm:text-[11.5px] tracking-[0.32em] uppercase font-semibold text-forest/55"
        >
          Trusted by our partners
        </p>
      </Reveal>
      <div className="pb-16 sm:pb-20 partners-fade">
        <div
          className="partners-track"
          data-testid="partners-track"
          style={{ "--partner-count": count }}
        >
          <Strip partners={partners} offset={0} />
          <Strip partners={partners} offset={partners.length} hidden />
        </div>
      </div>
    </section>
  );
};
